import { Spacing } from '@persona-id/design-tokens';
import ChevronDoubleLeftIcon from '@persona-id/design-tokens/icons/ChevronDoubleLeftIcon';
import { FC, RefObject, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import { NavAndPanelsOverlayContext } from 'dashboard/components-lib/NavAndPanelsOverlay/NavAndPanelsOverlay';
import ToolbarControlButton from 'dashboard/components-lib/ToolbarControlButton';

export interface LeftPanelSharedProps extends CollapseButtonProps {
  title: string;
}

export const LeftPanel = styled('div')`
  position: relative;
  height: 100%;
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  background-color: ${({ theme }) => theme.color.background.primary};
  display: flex;
  flex-direction: column;

  // Combats the pointer-events:none on the parent overlay to allow interacting with the panel content
  pointer-events: auto;
`;

export const PanelTitle = styled('div')`
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  font-weight: ${({ theme }) => theme.text.paragraph.lgBold.fontWeight};
  line-height: ${({ theme }) => theme.text.paragraph.lgBold.lineHeight};
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

export const PanelTitleActions = styled('div')`
  height: 100%;
  padding: 0 ${Spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing[1]};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
`;

export const PanelContent = styled('div')`
  min-height: 0;
  display: flex;
`;

export const PanelContentScrollContainer = styled('div')`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

interface CollapseButtonProps {
  /** Path to navigate to when the collapse button is clicked */
  basePath?: string;
}

export const CollapseButton: FC<CollapseButtonProps> = ({ basePath }) => {
  const navigate = useNavigate();
  const { collapseNavItem } = useContext(NavAndPanelsOverlayContext);
  return (
    <ToolbarControlButton
      id='panel__collapse-button'
      tooltipText='Close panel'
      onClick={() => {
        collapseNavItem();
        if (basePath) {
          navigate(basePath);
        }
      }}
      icon={<ChevronDoubleLeftIcon />}
    />
  );
};

const DragHandleContainer = styled.div`
  cursor: col-resize;
  width: ${Spacing[2]};
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  right: -${Spacing[2]};
`;

interface HorizontalDragHandlerProps {
  onMoveTo: (x: number) => void;
  // Used for calculating offset
  panelRef: RefObject<HTMLDivElement>;
}

export const HorizontalDragHandler: FC<HorizontalDragHandlerProps> = ({ onMoveTo, panelRef }) => {
  const [isDragging, setIsDragging] = useState(false);
  // The x distance from the right edge of the panel to where the cursor started dragging
  const [xOffset, setXOffset] = useState(0);

  useEffect(() => {
    const resize = (event: MouseEvent) => {
      if (isDragging) {
        const updatedPanelRightEdgeXPosition = event.clientX + xOffset;
        onMoveTo(updatedPanelRightEdgeXPosition);
      }
    };

    const stopDragging = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopDragging);
    return () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopDragging);
    };
  }, [isDragging, onMoveTo, xOffset]);

  return (
    <DragHandleContainer
      aria-label='Drag to resize'
      onMouseDown={(e) => {
        if (panelRef.current) {
          setXOffset(panelRef.current.getBoundingClientRect().right - e.clientX);
        }

        setIsDragging(true);
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseUp={() => {
        setIsDragging(false);
        setXOffset(0);
      }}
      onMouseOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    />
  );
};
